import { RichTextField } from "../../ui/RichTextField";
import { useEffect, useMemo, useState } from "react";
import { Fingerprint, Plus, RefreshCcw, ShieldCheck, ShieldOff, SearchCheck, Trash2 } from "lucide-react";
import { Dialog } from "../../ui/layers";
import { useKeysTrustManagedCopy } from "./keysTrustManagedCatalog";
import { useTrustAuthorityCopy } from "./trustAuthorityCatalog";
import { useManagedSigningExperienceCopy } from "./managedSigningExperienceCatalog";

import {
  activateManagedSigningIdentity,
  getManagedSigningRevocationPreflight,
  listManagedSigningIdentities,
  retireManagedSigningIdentity,
  revokeManagedSigningIdentity,
  stageManagedSigningIdentity,
  type GeneratableManagedSigningPurpose,
  type ManagedSigningIdentity,
  type ManagedSigningRevocationPreflight,
} from "./keysTrustApi";
import "../../styles/keys-trust-managed.css";

function shortFingerprint(value: string): string {
  return `${value.slice(0, 12)}…${value.slice(-12)}`;
}

function storageKey(purpose: GeneratableManagedSigningPurpose): string {
  return `kavoosh.keysTrust.stage.${purpose}`;
}

function operationId(purpose: GeneratableManagedSigningPurpose): string {
  const key = storageKey(purpose);
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const next = window.crypto.randomUUID();
  window.sessionStorage.setItem(key, next);
  return next;
}

export function ManagedSigningPanel({ onSessionUnavailable }: { onSessionUnavailable?: () => void }) {
  const c=useManagedSigningExperienceCopy();
  const {t}=useKeysTrustManagedCopy();
  const {t:a}=useTrustAuthorityCopy();
  const [confirmation,setConfirmation]=useState<"generate"|ManagedSigningIdentity|null>(null);
  const [preflightId,setPreflightId]=useState<string|null>(null);
  const [items, setItems] = useState<ManagedSigningIdentity[]>([]);
  const purpose: GeneratableManagedSigningPurpose = "collector_release_signer";
  const [retiringId, setRetiringId] = useState<string | null>(null);
  const [retirementReason, setRetirementReason] = useState("");
  const [revocationChecks, setRevocationChecks] = useState<Record<string, ManagedSigningRevocationPreflight>>({});
  const [checkingRevocationId, setCheckingRevocationId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revocationReason, setRevocationReason] = useState("");
  const [revocationAcknowledged, setRevocationAcknowledged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setItems(await listManagedSigningIdentities());
      setRevocationChecks({});
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : c("error");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const collectorItems = useMemo(
    () => items.filter((item) => item.purpose === "collector_release_signer"),
    [items],
  );
  async function generate() {
    setBusy(true);
    setError(null);
    setMessage(null);
    const id = operationId(purpose);
    try {
      const created = await stageManagedSigningIdentity({ operationId: id, purpose });
      window.sessionStorage.removeItem(storageKey(purpose));
      setItems((current) => [created, ...current.filter((item) => item.operation_id !== created.operation_id)]);
      setMessage(c("changed"));
      setConfirmation(null);
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : c("error");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBusy(false);
    }
  }

  async function activate(item: ManagedSigningIdentity) {
    if (item.lifecycle !== "staged" || item.purpose !== "collector_release_signer") return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const activated = await activateManagedSigningIdentity(item.operation_id);
      setItems((current) => current.map((value) => (
        value.operation_id === activated.operation_id ? activated : value
      )));
      setMessage(c("changed"));
      setConfirmation(null);
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : c("error");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBusy(false);
    }
  }

  async function retire(item: ManagedSigningIdentity) {
    if (item.lifecycle !== "active") return;
    const reason = retirementReason.trim();
    if (reason.length < 8 || reason.length > 500) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const value = await retireManagedSigningIdentity(item.operation_id, reason);
      setItems((current) => current.map((identity) => (
        identity.operation_id === value.operation_id ? value : identity
      )));
      setRetiringId(null);
      setRetirementReason("");
      setRevocationChecks((current) => {
        const next = { ...current };
        delete next[item.operation_id];
        return next;
      });
      setMessage(c("changed"));
    } catch (reasonValue) {
      const detail = reasonValue instanceof Error ? reasonValue.message : c("error");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBusy(false);
    }
  }

  async function checkRevocation(item: ManagedSigningIdentity) {
    if (item.lifecycle !== "retired" && item.lifecycle !== "revocation_pending") return;
    setPreflightId(item.operation_id);
    setCheckingRevocationId(item.operation_id);
    setError(null);
    setMessage(null);
    try {
      const value = await getManagedSigningRevocationPreflight(item.operation_id);
      setRevocationChecks((current) => ({ ...current, [item.operation_id]: value }));
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : c("error");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setCheckingRevocationId(null);
    }
  }

  async function revoke(item: ManagedSigningIdentity) {
    const check = revocationChecks[item.operation_id];
    const reason = revocationReason.trim();
    if (
      !check?.revocation_allowed ||
      !revocationAcknowledged ||
      reason.length < 8 ||
      reason.length > 500
    ) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await revokeManagedSigningIdentity(item.operation_id, reason);
      setItems((current) => current.map((identity) => (
        identity.operation_id === result.identity.operation_id ? result.identity : identity
      )));
      setRevokingId(null);
      setRevocationReason("");
      setRevocationAcknowledged(false);
      setRevocationChecks((current) => {
        const next = { ...current };
        delete next[item.operation_id];
        return next;
      });
      setMessage(c("changed"));
    } catch (reasonValue) {
      const detail = reasonValue instanceof Error ? reasonValue.message : c("error");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBusy(false);
    }
  }

  function beginRetirement(item: ManagedSigningIdentity) {
    if (item.lifecycle !== "active") return;
    setRetiringId(item.operation_id);
    setRetirementReason("");
    setError(null);
    setMessage(null);
  }

  function beginRevocation(item: ManagedSigningIdentity) {
    if (!revocationChecks[item.operation_id]?.revocation_allowed) return;
    setPreflightId(null);
    setRevokingId(item.operation_id);
    setRevocationReason("");
    setRevocationAcknowledged(false);
    setError(null);
    setMessage(null);
  }

  const lifecycle = (item:ManagedSigningIdentity) => item.lifecycle === "active" ? a("active") : c(item.lifecycle === "staged" ? "staged" : item.lifecycle === "retired" ? "retired" : item.lifecycle === "revoked" ? "revoked" : item.lifecycle === "revocation_pending" ? "revocationPending" : "recoveryPending");
  const retiring=collectorItems.find(item=>item.operation_id===retiringId);
  const revoking=collectorItems.find(item=>item.operation_id===revokingId);
  const preflight=preflightId ? revocationChecks[preflightId] : undefined;
  const cancel=()=>{if(busy)return;setConfirmation(null);setRetiringId(null);setRevokingId(null);setPreflightId(null);};
  const feedback=error ? <p role="alert" className="admin-v12-form-error">{error}</p> : null;
  return <section className="keys-trust-managed keys-signing-directory" aria-labelledby="managed-signing-title">
    <header><div><h2 id="managed-signing-title">{c("releaseSigning")}</h2><p>{c("description")}</p></div><button type="button" className="admin-v12-secondary" onClick={()=>void load()} disabled={loading||busy}><RefreshCcw size={15}/>{t(loading ? "loading" : "refresh")}</button></header>
    <div className="keys-signing-toolbar"><span>{t("custodyBody")}</span><button type="button" className="admin-v12-primary" onClick={()=>setConfirmation("generate")} disabled={busy}><Plus size={16}/>{c("generate")}</button></div>
    {feedback}{message && <p role="status" className="keys-trust-managed-success">{message}</p>}
    <div className="admin-v12-table-wrap"><table><thead><tr><th>{t("columnAuthority")}</th><th>{t("columnState")}</th><th>{t("columnCustody")}</th><th>{t("version")}</th><th>{t("currentStep")}</th></tr></thead><tbody>{collectorItems.map(item=><tr key={item.operation_id}>
      <td><strong><Fingerprint size={16}/> {item.fingerprint_sha256 ? shortFingerprint(item.fingerprint_sha256) : t("pending")}</strong><small>{item.algorithm}</small><details><summary>{t("fingerprintLabel")}</summary><code>{item.fingerprint_sha256??t("pending")}</code><dl><dt>{t("keyId")}</dt><dd><code>{item.key_id??t("pending")}</code></dd><dt>{t("columnUsedBy")}</dt><dd>{item.consumers.join(" · ")}</dd></dl></details></td>
      <td><b className={`keys-trust-managed-state state-${item.lifecycle}`}>{lifecycle(item)}</b></td><td><code>{item.provider_kind}</code><small>{item.provider_reference}</small></td><td>{item.version}</td>
      <td>{item.lifecycle === "staged" && <button type="button" className="admin-v12-secondary" onClick={()=>setConfirmation(item)} disabled={busy}><ShieldCheck size={14}/>{c("activate")}</button>}{item.lifecycle === "active" && <button type="button" className="admin-v12-secondary" onClick={()=>beginRetirement(item)} disabled={busy}><ShieldOff size={14}/>{c("retire")}</button>}{["retired","revocation_pending"].includes(item.lifecycle) && <button type="button" className="admin-v12-secondary" onClick={()=>void checkRevocation(item)} disabled={busy||checkingRevocationId!==null}><SearchCheck size={14}/>{c("eligibility")}</button>}</td>
    </tr>)}</tbody></table></div>
    {!loading && collectorItems.length===0 && <p className="admin-v12-empty">{c("empty")}</p>}
    <aside className="keys-signing-rotation"><ShieldCheck size={18}/><p>{c("rotationBody")}</p></aside>
    <Dialog open={confirmation!==null} onDismiss={cancel} dismissible={!busy} title={c(confirmation==="generate" ? "generateTitle" : "activateTitle")} description={c("description")} footer={<><button type="button" className="admin-v12-secondary" onClick={cancel} disabled={busy}>{c("cancel")}</button><button type="button" className="admin-v12-primary" disabled={busy} onClick={()=>void (async()=>{if(confirmation==="generate")await generate();else if(confirmation)await activate(confirmation);})()}>{busy?t("working"):c(confirmation==="generate"?"generate":"activate")}</button></>}>
      <p>{t("boundaryBody")}</p>{confirmation!=="generate"&&<p>{t("tufRecentMfaBoundary")}</p>}{confirmation && confirmation!=="generate" && <code>{confirmation.fingerprint_sha256}</code>}{feedback}
    </Dialog>
    <Dialog open={!!retiring} onDismiss={cancel} dismissible={!busy} title={c("retireTitle")} description={c("retireBody")} footer={<><button type="button" className="admin-v12-secondary" disabled={busy} onClick={cancel}>{c("cancel")}</button><button type="button" className="admin-v12-primary" disabled={busy||retirementReason.trim().length<8||retirementReason.trim().length>500} onClick={()=>retiring&&void retire(retiring)}>{busy?t("working"):c("confirmRetire")}</button></>}>
      <p>{t("tufRecentMfaBoundary")}</p><code>{retiring?.fingerprint_sha256}</code><RichTextField label={c("retireReason")} value={retirementReason} onChange={e=>setRetirementReason(e.currentTarget.value)} minLength={8} maxLength={500} rows={3} disabled={busy}/>{feedback}
    </Dialog>
    <Dialog open={preflightId!==null} onDismiss={cancel} dismissible={!busy} title={preflight ? c(preflight.revocation_allowed?"clear":"blocked"):t("checking")} footer={<><button type="button" className="admin-v12-secondary" onClick={cancel}>{c("cancel")}</button>{preflight?.revocation_allowed && <button type="button" className="admin-v12-primary keys-trust-danger" onClick={()=>{const item=collectorItems.find(i=>i.operation_id===preflightId);if(item)beginRevocation(item);}}>{c("revokeTitle")}</button>}</>}>
      {preflight && <><p>{c("dependencyCount",{count:preflight.dependency_count})}</p><p>{c("revokeBody")}</p><ul>{preflight.blocking_dependencies.map(d=><li key={d.record_id}><code>{d.release_key}</code> · <code>{d.status}</code> · {t("version")} {d.version}</li>)}</ul></>}{feedback}
    </Dialog>
    <Dialog open={!!revoking} onDismiss={cancel} dismissible={!busy} title={c("revokeTitle")} description={c("revokeBody")} footer={<><button type="button" className="admin-v12-secondary" disabled={busy} onClick={cancel}>{c("cancel")}</button><button type="button" className="admin-v12-primary keys-trust-danger" disabled={busy||!revocationAcknowledged||revocationReason.trim().length<8||revocationReason.trim().length>500} onClick={()=>revoking&&void revoke(revoking)}><Trash2 size={14}/>{busy?t("working"):c("confirmRevoke")}</button></>}>
      <p>{t("tufRecentMfaBoundary")}</p><code>{revoking?.fingerprint_sha256}</code><RichTextField label={c("revokeReason")} value={revocationReason} onChange={e=>setRevocationReason(e.currentTarget.value)} minLength={8} maxLength={500} rows={3} disabled={busy}/><label className="keys-trust-revoke-confirmation"><input type="checkbox" checked={revocationAcknowledged} onChange={e=>setRevocationAcknowledged(e.currentTarget.checked)} disabled={busy}/><span>{c("ack")}</span></label>{feedback}
    </Dialog>
  </section>;
}
